import { inject, Injectable } from '@angular/core';
import { ApiClient } from './api-client';
import { SHARED_CONFIG } from '../../../environments/shared-config.token';
import { Observable } from 'rxjs';
import { Endpoints } from './endpoints';
import { ApplicationTaskAttachment } from '../models/attachment/application-task-attachment';
import { MembershipApplicationTask } from '../models/member/membership-application';
import { MemberEmployer } from '../models/member/member-employer';
import { OdataQueryOptions } from '../models/odata/odata-query-options';

@Injectable({
  providedIn: 'root',
})
export class SharedMemberApi {
  private apiClient = inject(ApiClient);
  private config = inject(SHARED_CONFIG);

  updateMembershipApplicationTask(
    request: MembershipApplicationTask,
    applicationTaskId: number,
  ): Observable<MembershipApplicationTask> {
    return this.apiClient.put(
      `${this.config.apiUrl}${Endpoints.MembershipApplicationTask.update(applicationTaskId)}`,
      request,
    );
  }

  updateMembershipReapplicationApplicationTask(
    request: MembershipApplicationTask,
    applicationTaskId: number,
  ): Observable<MembershipApplicationTask> {
    return this.apiClient.put(
      `${this.config.apiUrl}${Endpoints.MembershipReapplicationApplicationTask.update(applicationTaskId)}`,
      request,
    );
  }

  updateMembershipReinstatementApplicationTask(
    request: MembershipApplicationTask,
    applicationTaskId: number,
  ): Observable<MembershipApplicationTask> {
    return this.apiClient.put(
      `${this.config.apiUrl}${Endpoints.MembershipReinstatementApplicationTask.update(applicationTaskId)}`,
      request,
    );
  }

  addMembershipApplicationTaskUpload(taskAttachment: ApplicationTaskAttachment): Observable<ApplicationTaskAttachment> {
    return this.apiClient.post(
      `${this.config.apiUrl}${Endpoints.MembershipApplicationTask.addUpload(taskAttachment.membershipApplicationTaskId ?? -1)}`,
      taskAttachment,
    );
  }

  removeMembershipApplicationTaskUpload(applicationTaskId: number, uploadId: number): Observable<void> {
    return this.apiClient.delete(
      `${this.config.apiUrl}${Endpoints.MembershipApplicationTask.removeUpload(applicationTaskId, uploadId)}`,
    );
  }

  addMembershipReinstatementApplicationTaskUpload(
    taskAttachment: ApplicationTaskAttachment,
  ): Observable<ApplicationTaskAttachment> {
    return this.apiClient.post(
      `${this.config.apiUrl}${Endpoints.MembershipReinstatementApplicationTask.addUpload(taskAttachment.membershipReinstatementApplicationTaskId ?? -1)}`,
      taskAttachment,
    );
  }

  removeMembershipReinstatementApplicationTaskUpload(applicationTaskId: number, uploadId: number): Observable<void> {
    return this.apiClient.delete(
      `${this.config.apiUrl}${Endpoints.MembershipReinstatementApplicationTask.removeUpload(applicationTaskId, uploadId)}`,
    );
  }

  addMembershipReapplicationTaskUpload(
    taskAttachment: ApplicationTaskAttachment,
  ): Observable<ApplicationTaskAttachment> {
    return this.apiClient.post(
      `${this.config.apiUrl}${Endpoints.MembershipReapplicationApplicationTask.addUpload(taskAttachment.membershipReapplicationApplicationTaskId ?? -1)}`,
      taskAttachment,
    );
  }

  removeMembershipReapplicationTaskUpload(applicationTaskId: number, uploadId: number): Observable<void> {
    return this.apiClient.delete(
      `${this.config.apiUrl}${Endpoints.MembershipReapplicationApplicationTask.removeUpload(applicationTaskId, uploadId)}`,
    );
  }

  //region Member Employer
  getMemberEmployers(memberId: number): Observable<MemberEmployer[]> {
    return this.apiClient.get(`${this.config.apiUrl}${Endpoints.MemberEmployer.getMemberEmployers(memberId)}`);
  }
  getMemberEmployerById(
    memberId: number,
    memberEmployerId: number,
    odataQueryOptions: OdataQueryOptions | null = null,
  ): Observable<MemberEmployer> {
    const httpParams = odataQueryOptions?.toHttpParams();
    return this.apiClient.get(
      `${this.config.apiUrl}${Endpoints.MemberEmployer.getMemberEmployer(memberId, memberEmployerId)}`,
      httpParams,
    );
  }
  createMemberEmployer(memberId: number, request: MemberEmployer): Observable<MemberEmployer> {
    return this.apiClient.post(`${this.config.apiUrl}${Endpoints.MemberEmployer.create(memberId)}`, request);
  }
  updateMemberEmployer(
    memberId: number,
    memberEmployerId: number,
    request: MemberEmployer,
  ): Observable<MemberEmployer> {
    return this.apiClient.put(
      `${this.config.apiUrl}${Endpoints.MemberEmployer.update(memberId, memberEmployerId)}`,
      request,
    );
  }
}
